/**
 * LINK HANDLER COMPONENT
 * 
 * This component provides consistent external link handling with session persistence.
 * It ensures that when users open links in new tabs, they remain logged in.
 * 
 * Features:
 * - Opens external links in new tabs
 * - Maintains session persistence across tabs
 * - Provides consistent link behavior throughout the app
 * - Handles both internal and external links appropriately
 * - Includes proper security attributes (noopener, noreferrer)
 * 
 * Usage:
 * <LinkHandler url="https://example.com" className="link-button">
 *   Click to open in new tab
 * </LinkHandler>
 * 
 * Or as a wrapper:
 * <LinkHandler url="https://example.com">
 *   <button>Open Link</button>
 * </LinkHandler>
 */

import React, { useCallback, useRef } from 'react';
import { openLinkInNewTab } from '../utils/sessionPersistence';

const LinkHandler = ({ 
  url, 
  children, 
  className = '', 
  style = {}, 
  onClick = null,
  disabled = false,
  title = '',
  'aria-label': ariaLabel = '',
  ...props 
}) => {
  const linkRef = useRef(null);

  const handleClick = useCallback((event) => {
    // Prevent default behavior if this is a wrapper component
    if (event.target !== linkRef.current) {
      event.preventDefault();
    }

    // Call custom onClick if provided
    if (onClick) {
      onClick(event);
    }

    // Don't open link if disabled
    if (disabled) {
      return;
    }

    // Validate URL
    if (!url) {
      console.warn('LinkHandler: No URL provided');
      return;
    }

    // Check if URL is valid
    try {
      new URL(url);
    } catch (error) {
      console.error('LinkHandler: Invalid URL provided:', url);
      return;
    }

    // Open link in new tab with session persistence
    const success = openLinkInNewTab(url, {
      noopener: true,
      noreferrer: true
    });

    if (!success) {
      console.error('LinkHandler: Failed to open link in new tab');
    }
  }, [url, onClick, disabled]);

  // If no children provided, render as a clickable element
  if (!children) {
    return (
      <button
        ref={linkRef}
        className={`link-handler-button ${className}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          ...style
        }}
        onClick={handleClick}
        disabled={disabled}
        title={title || `Open ${url} in new tab`}
        aria-label={ariaLabel || `Open ${url} in new tab`}
        {...props}
      >
        {url}
      </button>
    );
  }

  // If children provided, wrap them
  return (
    <div
      ref={linkRef}
      className={`link-handler-wrapper ${className}`}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...style
      }}
      onClick={handleClick}
      title={title || `Open ${url} in new tab`}
      aria-label={ariaLabel || `Open ${url} in new tab`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick(event);
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// Higher-order component for easy integration
export const withLinkHandler = (WrappedComponent) => {
  return React.forwardRef(({ url, onLinkClick, ...props }, ref) => {
    const handleClick = useCallback((event) => {
      if (onLinkClick) {
        onLinkClick(event);
      }
      
      if (url) {
        openLinkInNewTab(url, {
          noopener: true,
          noreferrer: true
        });
      }
    }, [url, onLinkClick]);

    return (
      <WrappedComponent
        ref={ref}
        {...props}
        onClick={handleClick}
      />
    );
  });
};

// Hook for programmatic link opening
export const useLinkHandler = () => {
  const openLink = useCallback((url, options = {}) => {
    if (!url) {
      console.warn('useLinkHandler: No URL provided');
      return false;
    }

    try {
      new URL(url);
    } catch (error) {
      console.error('useLinkHandler: Invalid URL provided:', url);
      return false;
    }

    return openLinkInNewTab(url, {
      noopener: true,
      noreferrer: true,
      ...options
    });
  }, []);

  return { openLink };
};

// Utility function for direct link opening
export const openExternalLink = (url, options = {}) => {
  if (!url) {
    console.warn('openExternalLink: No URL provided');
    return false;
  }

  try {
    new URL(url);
  } catch (error) {
    console.error('openExternalLink: Invalid URL provided:', url);
    return false;
  }

  return openLinkInNewTab(url, {
    noopener: true,
    noreferrer: true,
    ...options
  });
};

export default LinkHandler;

